import { z } from "zod";
import { AgencyTypeFieldsSchema, AgencyTypeSchema } from "../shared";

// ==================
// Enums
// ==================

/** Lead status: pending awaiting matching, matched when fully assigned */
export const LeadStatusEnum = z.enum(["pending", "matched"]);
export type LeadStatus = z.infer<typeof LeadStatusEnum>;

/** How the expert was matched to the lead */
export const MatchTypeEnum = z.enum(["client_selected", "admin_matched"]);
export type MatchType = z.infer<typeof MatchTypeEnum>;

/** How the client wants to be matched with experts */
export const MatchingStrategyEnum = z.enum(["selected", "selected-auto", "auto"]);
export type MatchingStrategy = z.infer<typeof MatchingStrategyEnum>;

/** Project urgency level (includes legacy values for backward compatibility) */
export const UrgencyEnum = z.enum([
    // Legacy values (for backward compatibility)
    "urgent",
    "standard",
    // New values
    "as_soon_as_possible",
    "within_the_next_week",
    "within_the_next_month",
    "open_to_expert_opinion",
]);
export type Urgency = z.infer<typeof UrgencyEnum>;

/** Display labels for each urgency value */
export const UrgencyLabels: Record<Urgency, string> = {
    urgent: "Urgent",
    standard: "Standard",
    as_soon_as_possible: "As soon as possible",
    within_the_next_week: "Within the next week",
    within_the_next_month: "Within the next month",
    open_to_expert_opinion: "Open to expert opinion",
};

/** Only the new urgency values (for new form submissions) */
export const NewUrgencyValues = [
    "as_soon_as_possible",
    "within_the_next_week",
    "within_the_next_month",
    "open_to_expert_opinion",
] as const;

/** Form options for urgency select/radio components */
export const UrgencyFormOptions = NewUrgencyValues.map((value) => ({
    value,
    label: UrgencyLabels[value],
}));

/** Helper to get label from urgency value */
export function getUrgencyLabel(urgency: Urgency): string {
    return UrgencyLabels[urgency] ?? urgency;
}

/** Expert account type */
export const ExpertTypeEnum = z.enum(["freelancer", "agency"]);
export type ExpertType = z.infer<typeof ExpertTypeEnum>;

// ==================
// Media Schema
// ==================

/** Full media object for avatars and images */
export const LeadMediaSchema = z.object({
    id: z.string(),
    key: z.string(),
    filename: z.string(),
    url: z.url(),
    cdn_url: z.url(),
    mime_type: z.string(),
    size: z.number(),
    formatted_size: z.string(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    is_temporary: z.boolean(),
    collection: z.string(),
    created_at: z.string(),
    metadata: z.unknown().nullable(),
});

export type LeadMedia = z.infer<typeof LeadMediaSchema>;

// ==================
// Store Schema
// ==================

/** Store information */
export const StoreSchema = z.object({
    id: z.number(),
    title: z.string(),
    url: z.string(),
});

export type Store = z.infer<typeof StoreSchema>;

// ==================
// Client Schema (for Expert/Admin views)
// ==================

/** Client information visible to experts and admins */
export const LeadClientSchema = z.object({
    id: z.number(),
    name: z.string(),
    owner_name: z.string(),
    /** Owner email - can be empty string or null if not set */
    owner_email: z.string().nullable(),
});

export type LeadClient = z.infer<typeof LeadClientSchema>;

// ==================
// User Minimal Schema
// ==================

/** Minimal user info for matched_by field */
export const UserMinimalSchema = z.object({
    id: z.number(),
    name: z.string(),
});

export type UserMinimal = z.infer<typeof UserMinimalSchema>;

// ==================
// Expert Schemas (different views for different roles)
// ==================

/** Basic expert info (used in client views - no email) */
export const ExpertMinimalSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    slug: z.string(),
    type: ExpertTypeEnum,
    agency_type: AgencyTypeSchema.optional(),
    agency_type_fields: AgencyTypeFieldsSchema.optional(),
    avatar: z.string().nullable(),
    avatar_media: LeadMediaSchema.nullable(),
});

export type ExpertMinimal = z.infer<typeof ExpertMinimalSchema>;

/** Extended expert info with email (used in admin views) */
export const ExpertWithEmailSchema = ExpertMinimalSchema.extend({
    /** Expert email - can be empty string or null if not set */
    email: z.string().nullable(),
});

export type ExpertWithEmail = z.infer<typeof ExpertWithEmailSchema>;

export const AdminQueueClientSchema = z.object({
    id: z.number(),
    name: z.string(),
    owner_email: z.string().nullable(),
});
export type AdminQueueClient = z.infer<typeof AdminQueueClientSchema>;

export const AdminQueueActionsSchema = z.object({
    can_match: z.boolean(),
    can_unmatch: z.boolean(),
    can_override_project_status: z.boolean(),
    can_override_quote_status: z.boolean(),
    can_override_quote_payment_status: z.boolean(),
});
export type AdminQueueActions = z.infer<typeof AdminQueueActionsSchema>;

// ==================
// Lead Match Schemas (different for each role)
// ==================

/** Match for client view (no email, no matched_by) */
export const LeadMatchClientSchema = z.object({
    id: z.number().int(),
    expert: ExpertMinimalSchema,
    match_type: MatchTypeEnum,
    matched_at: z.string(),
});

export type LeadMatchClient = z.infer<typeof LeadMatchClientSchema>;

/** Match for admin view (includes email and who matched) */
export const LeadMatchAdminSchema = z.object({
    id: z.number().int(),
    expert: ExpertWithEmailSchema,
    matched_by: UserMinimalSchema,
    match_type: MatchTypeEnum,
    matched_at: z.string(),
});

export type LeadMatchAdmin = z.infer<typeof LeadMatchAdminSchema>;

/** Match for expert view (only their own match info, no expert details) */
export const LeadMatchExpertSchema = z.object({
    id: z.number(),
    match_type: MatchTypeEnum,
    matched_at: z.string(),
});

export type LeadMatchExpert = z.infer<typeof LeadMatchExpertSchema>;
